"use client";
import { setRedirectAfterLogin } from "@/app/page/rootComponents/AuthHubListener";
import { FlexRowAlignCenter } from "@/components/Flex";
import ClickableText from "@/components/styled/button/ClickableText";
import { useMounted } from "@/hooks/useMounted";
import { report } from "@/utils/analytics/logging";
import { cleanEmail, signIn, usernameFromEmail } from "@knowt/syncing/hooks/user/auth";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { createQueryStrings } from "@knowt/syncing/utils/genericUtils";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "@bprogress/next";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { AuthEvent } from "../AuthPopup/utils/constants";
import { useAuthSlideContextSelector } from "../AuthSlide/hook/useAuthSlideContext";
import {
    AuthLeftContainer,
    AuthLeftInnerContainer,
    AuthMainContainer,
    AuthRightContainer,
} from "../components/styled/Containers";
import { AuthForm, AuthInputWithLabel, AuthSubmitButton } from "../components/styled/Forms";
import { GapPerSection, OrDivider } from "../components/styled/Separator";
import { AuthSignupSocialBtns } from "../components/styled/SocialButtons";
import { AuthDescription, AuthTitle } from "../components/styled/Typographies";
import { SS_MANUALLY_VERIFY_SIGNIN_KEY } from "../utils";
import clsx from "clsx";
import br from "@/styles/breakpoints.module.css";
import AuthKnowtLogo from "../components/AuthKnowtLogo";
import loginAnimationDark from "@/public/images/lottie/sign-up-expecting-you-dark.json";
import loginAnimationLight from "@/public/images/lottie/sign-up-expecting-you-light.json";
import { useThemeContextSelector } from "@/utils/theme";

const LoginPage = ({ prefilledEmail, onClose = () => null }: { prefilledEmail: string; onClose?: () => void }) => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);
    const { loginInProgress } = useCurrentUser();
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const mounted = useMounted();

    const openAuthSlide = useAuthSlideContextSelector(state => state.openAuthSlide);

    const [authStatus, setAuthStatus] = useState<"loading" | "success" | "failure" | null>(null);
    const [email, setEmail] = useState(prefilledEmail || "");
    const [password, setPassword] = useState("");

    const isAuthLoading = authStatus === "loading";

    const signInUser = async () => {
        if (isAuthLoading || loginInProgress || !mounted) {
            return;
        }

        const cleanedEmail = cleanEmail(email);

        if (!cleanedEmail.length && !password.length) {
            toast.error("Form cannot be empty.");
            return;
        }

        if (!cleanedEmail.replace(/[0-9]/g, "")) {
            toast.error("Usernames with all numbers have been disabled. Please use your email to sign in.");
            return;
        }

        setAuthStatus("loading");
        setRedirectAfterLogin(pathname, searchParams);
        signIn(cleanedEmail, password)
            .then(() => {
                setAuthStatus("success");
                onClose();
            })
            .catch(err => {
                setAuthStatus("failure");
                if (err.message === "UserNotConfirmedException") {
                    toast.error("Your account has not been verified yet.");
                    window?.sessionStorage.setItem(
                        SS_MANUALLY_VERIFY_SIGNIN_KEY,
                        JSON.stringify({ username: usernameFromEmail(cleanedEmail), password })
                    );
                    router.push(`/verify${createQueryStrings({ username: cleanedEmail })}`);
                } else if (err.message === "PasswordResetRequiredException") {
                    toast.error(
                        "A reset is required for the account. Please check your email for the verification code."
                    );
                    router.push(`/reset${createQueryStrings({ username: cleanedEmail })}`);
                } else if (err.message === "UserNotFoundException") {
                    toast.error(
                        "We couldn't find an account with this email! If you are sure you used this email, maybe try one of the social sign in buttons above! If this was a mistake, please contact us at knowt.com/contact."
                    );
                } else if (err.message === "NotAuthorizedException") {
                    toast.error("Your credentials were incorrect. Please try typing in your email or password again!");
                } else {
                    report(err, "signInUser");
                    toast.error("Something went wrong, please try again.");
                }

                setTimeout(() => setAuthStatus(null), 1000);
            });
    };

    return (
        <AuthMainContainer>
            <AuthLeftContainer style={{ justifyContent: "center", paddingBlock: "0rem" }}>
                <AuthLeftInnerContainer>
                    <AuthKnowtLogo onClose={onClose} />
                    <AuthTitle style={{ marginTop: "5rem", marginBottom: "1rem" }}>Welcome back!</AuthTitle>
                    <AuthDescription style={{ marginBottom: "3.5rem" }}>
                        We've been waiting for your arrival.
                    </AuthDescription>

                    <AuthSignupSocialBtns event={AuthEvent.GENERAL_SIGNUP} />

                    <AuthForm onSubmit={signInUser}>
                        <GapPerSection />
                        <OrDivider />
                        <GapPerSection />

                        <AuthInputWithLabel
                            label="Email"
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder={"Enter your email"}
                            style={{ marginBottom: "1.6rem" }}
                        />
                        <AuthInputWithLabel
                            label="Password"
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder={"Enter your password"}
                        />
                        <FlexRowAlignCenter style={{ marginTop: "1rem" }}>
                            <ClickableText
                                className={clsx("bodyBold2", br.smDownTextAlignCenter)}
                                tabIndex={-1}
                                style={{ fontSize: "1.3rem" }}
                                onClick={() => openAuthSlide({ showResetPage: true })}>
                                Forgot your password? Reset it
                            </ClickableText>
                        </FlexRowAlignCenter>

                        <GapPerSection height="4rem" />
                        <AuthSubmitButton authStatus={authStatus}>Login</AuthSubmitButton>
                    </AuthForm>

                    <GapPerSection height="1.6rem" />

                    <ClickableText
                        className={clsx("bodyBold2")}
                        style={{ textAlign: "center" }}
                        tabIndex={-1}
                        onClick={() => openAuthSlide({ showSignupPage: true })}>
                        Don’t have an account? Sign up
                    </ClickableText>
                </AuthLeftInnerContainer>
            </AuthLeftContainer>

            <AuthRightContainer onClose={onClose} animation={isDarkMode ? loginAnimationDark : loginAnimationLight} />
        </AuthMainContainer>
    );
};

export default LoginPage;
